import { useEffect, useMemo, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { FilterMatchMode } from "primereact/api";
import { Tooltip } from "primereact/tooltip";
import { CONTENT_TYPE_OPTIONS } from "sj-ui-toolkit/src/constants/content";
import { formatMinutes } from "sj-ui-toolkit/src/utils/time";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import type { TrainingPlaAssignment } from "./types";

interface TrainingLessonRow {
  lessonKey: string;
  lessonName: string;
  lessonType: string | null;
  duration?: number | null;
  lastCweDetection: string | null;
}

const SIDEBAR_FILTERS = {
  lessonName: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
};

const lessonTypeLabels = Object.fromEntries(
  CONTENT_TYPE_OPTIONS.map(option => [option.value, option.label])
) as Record<string, string>;

lessonTypeLabels.CodingExercise = lessonTypeLabels.CodingExerciseItem || "Coding Exercise";

const lastCweTemplate = (rowData: TrainingLessonRow) =>
  rowData.lastCweDetection ? humanDateWithoutTime(rowData.lastCweDetection) : "--";

const durationTemplate = (rowData: TrainingLessonRow) =>
  rowData.duration ? `${rowData.duration} min` : "--";

const lessonTypeTemplate = (rowData: TrainingLessonRow) =>
  rowData.lessonType ? lessonTypeLabels[rowData.lessonType] || rowData.lessonType : "--";

interface TrainingLessonsSidebarProps {
  visible: boolean;
  onHide: () => void;
  assignment: TrainingPlaAssignment | null;
  selectedLessonKeys?: string[];
  onSave: (plaUuid: string, lessonKeys: string[]) => void;
  onSaveAndInclude: (plaUuid: string, lessonKeys: string[]) => void;
}

export default function TrainingLessonsSidebar({
  visible,
  onHide,
  assignment,
  selectedLessonKeys = [],
  onSave,
  onSaveAndInclude,
}: TrainingLessonsSidebarProps) {
  const [pendingSelection, setPendingSelection] = useState<TrainingLessonRow[]>([]);

  const lessonRows = useMemo(() => {
    if (!assignment) return [];

    const original = assignment._original;
    const sources = original.sources || [];

    return (original.lessons || [])
      .filter(lesson => lesson.lessonKey)
      .map(lesson => {
        const relevantSources = sources.filter(source =>
          source.lessonsRecommended?.includes(lesson.lessonKey ?? "")
        );
        const latestSource = relevantSources.reduce<{ createdAt?: string } | null>(
          (latest, source) => {
            if (!source.createdAt) return latest;
            return !latest || source.createdAt > (latest.createdAt ?? "") ? source : latest;
          },
          null
        );

        return {
          lessonKey: lesson.lessonKey || "",
          lessonName: lesson.lessonName || lesson.lessonKey || "",
          lessonType: lesson.lessonType || null,
          duration: typeof lesson.duration === "number" ? lesson.duration : null,
          lastCweDetection: latestSource?.createdAt ?? null,
        };
      });
  }, [assignment]);

  useEffect(() => {
    if (!visible) {
      setPendingSelection([]);
      return;
    }

    setPendingSelection(
      lessonRows.filter(row => row.lessonKey && selectedLessonKeys.includes(row.lessonKey))
    );
  }, [visible, lessonRows, selectedLessonKeys]);

  const totalSelectedMinutes = pendingSelection.reduce(
    (sum, row) => sum + (Number(row.duration) || 0),
    0
  );
  const noLessonsSelected = pendingSelection.length === 0;

  const headerContent = (
    <div>
      <h3 className="text-xl font-semibold">
        Personalized Assignment for {assignment?.displayName}
      </h3>
      <p className="mt-1 body-sm text-secondary">
        {pendingSelection.length} of {lessonRows.length} lessons selected
      </p>
      <p className="mt-1 body-sm text-secondary">
        Total duration: {formatMinutes(totalSelectedMinutes)}
      </p>
    </div>
  );

  const handleSave = () => {
    if (!assignment) return;
    onSave(
      assignment.uuid,
      pendingSelection
        .map(row => row.lessonKey)
        .filter((lessonKey): lessonKey is string => !!lessonKey)
    );
    onHide();
  };

  const handleSaveAndInclude = () => {
    if (!assignment) return;
    onSaveAndInclude(
      assignment.uuid,
      pendingSelection
        .map(row => row.lessonKey)
        .filter((lessonKey): lessonKey is string => !!lessonKey)
    );
    onHide();
  };

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={onHide}
      header={headerContent}
      style={{ width: "50rem" }}
      blockScroll
    >
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-auto">
          <DataTable
            value={lessonRows}
            selectionMode="multiple"
            selection={pendingSelection}
            onSelectionChange={event => setPendingSelection(event.value as TrainingLessonRow[])}
            metaKeySelection={false}
            dataKey="lessonKey"
            filters={SIDEBAR_FILTERS}
            sortMode="single"
            className="p-datatable-sm"
            aria-label="Lessons list"
            tableClassName="dt-responsive-text"
          >
            <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
            <Column
              field="lessonName"
              header="Lesson Name"
              sortable
              filter
              filterPlaceholder="Search by lesson name"
              showFilterMatchModes={false}
            />
            <Column field="lessonType" header="Lesson Type" body={lessonTypeTemplate} />
            <Column field="duration" header="Duration" sortable body={durationTemplate} />
            <Column
              field="lastCweDetection"
              header="Last CWE Detection"
              sortable
              body={lastCweTemplate}
            />
          </DataTable>
        </div>

        <div className="mt-4 flex justify-between border-t border-surface-700 pt-4">
          <Button label="Cancel" severity="secondary" onClick={onHide} />
          <div className="flex gap-2">
            <Button label="Save" outlined onClick={handleSave} />
            <Tooltip
              target=".save-include-btn-wrapper"
              content="This is disabled when no lessons are selected."
              position="top"
              disabled={!noLessonsSelected}
            />
            <span className="save-include-btn-wrapper">
              <Button
                label="Save and Include"
                onClick={handleSaveAndInclude}
                disabled={noLessonsSelected}
              />
            </span>
          </div>
        </div>
      </div>
    </Sidebar>
  );
}
